import type { ComponentProps } from 'react';
import React, { useCallback } from 'react';
import type { ViewProps } from 'react-native';
import { StyleSheet, View } from 'react-native';

import { theme } from '@garnish/constants';

import { useResponsive } from '../../../hooks';
import { BodyText } from '../../Text';
import {
  NOTICE_BANNER_AUTO_HIDE_TIMEOUT,
  NOTICE_BANNER_PADDING,
  NoticeBanner,
} from '../NoticeBanner';
import { NoticeToastStack } from '../NoticeToastStack';
import { NoticeBannersStackWrapper } from './components';
import { useNoticeBannersStack } from './NoticeBannersStackContext';
import { useNoticeBannersStackActions } from './NoticeBannersStackProvider';

import type { NoticeBannersStackItems } from './NoticeBannersStackProvider';

// ────────────────────────────────────────────────────────────────────────────────

export const NoticeBannersStack = (props: NoticeBannersStackProps) => {
  const {
    targetId,
    bannerPadding = NOTICE_BANNER_PADDING,
    bannerVariation,
    style,
    ...restProps
  } = props;

  const { stack, variant, isLiquidGlassEnabled, toastBottomOffset } = useNoticeBannersStack();
  const { pop, remove } = useNoticeBannersStackActions();
  const filteredStack = stack.filter((banner) => banner.targetId === targetId);
  const isToastVariant = variant === 'toast';
  const bannerStack = isToastVariant ? filteredStack.filter(checkIfBanner) : filteredStack;
  const toastStack = isToastVariant ? filteredStack.filter(checkIfToast) : [];
  const activeBanner = bannerStack[0];
  const hasToasts = toastStack.length > 0;

  // ─── Handlers ──────────────────────────────────────────────────────────────

  const removeBanner = useCallback(
    (bannerId: string | undefined) => {
      if (bannerId === undefined) {
        pop();

        return;
      }

      remove(bannerId);
    },
    [pop, remove],
  );

  const dismissToast = useCallback(
    (toast: NoticeToastItem) => {
      remove(toast.id);
      toast.onClose?.();
    },
    [remove],
  );

  // ───────────────────────────────────────────────────────────────────────────

  if (!activeBanner && !hasToasts) {
    return null;
  }

  return (
    <NoticeBannersStackWrapper style={style} {...restProps}>
      {activeBanner ? (
        <NoticeBannersStackActiveBanner
          banner={activeBanner}
          stack={bannerStack}
          bannerPadding={bannerPadding}
          bannerVariation={bannerVariation}
          onRemove={removeBanner}
        />
      ) : null}

      {hasToasts ? (
        <NoticeToastStack
          toasts={toastStack}
          placement="bottom"
          topGap={bannerPadding.top}
          bottomGap={toastBottomOffset}
          isLiquidGlassEnabled={isLiquidGlassEnabled}
          onDismiss={dismissToast}
        />
      ) : null}
    </NoticeBannersStackWrapper>
  );
};

// ────────────────────────────────────────────────────────────────────────────────

const NoticeBannersStackActiveBanner = (props: NoticeBannersStackActiveBannerProps) => {
  const { banner, stack, bannerPadding, bannerVariation, onRemove } = props;

  const {
    id,
    onClose,
    autoHideTimeout = NOTICE_BANNER_AUTO_HIDE_TIMEOUT,
    variation = bannerVariation,
    ...restBannerProps
  } = banner;

  // ─── HELPERS ─────────────────────────────────────────────────────

  const closeActiveBanner = useCallback(() => {
    onRemove(id);
    onClose?.();
  }, [id, onClose, onRemove]);

  return (
    <>
      <NoticeBanner
        key={id}
        autoHideTimeout={autoHideTimeout}
        onClose={closeActiveBanner}
        padding={bannerPadding}
        variation={variation}
        {...restBannerProps}
      />
      <NoticeBannersStackMoreBanners stack={stack} />
    </>
  );
};

// ────────────────────────────────────────────────────────────────────────────────

const NoticeBannersStackMoreBanners = (
  props: Readonly<{
    stack: NoticeBannersStackItems;
  }>,
) => {
  const { currentBreakpoint } = useResponsive();
  const { stack } = props;

  const wrapperResponsiveStyles = currentBreakpoint.isXS
    ? styles.moreBannersXSmall
    : styles.moreBannersSmallUp;
  const wrapperStyle = [styles.moreBanners, wrapperResponsiveStyles];

  return stack.length > 1 ? (
    <View style={wrapperStyle}>
      <View style={styles.moreBannersContent}>
        <BodyText sizeMatch={['12']} style={styles.moreBannersText}>
          +{stack.length - 1}
        </BodyText>
      </View>
    </View>
  ) : null;
};

// ─── HELPERS ────────────────────────────────────────────────────────────────────

function checkIfBanner(item: NoticeBannersStackItem) {
  return item.variant === 'banner';
}

function checkIfToast(item: NoticeBannersStackItem): item is NoticeToastItem {
  return item.variant !== 'banner' && item.id !== undefined;
}

// ─── STYLES ─────────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  moreBanners: {
    alignItems: 'center',
  },
  moreBannersXSmall: {
    marginBottom: theme.spacing['4'],
  },
  moreBannersSmallUp: {
    marginTop: theme.spacing['4'],
  },
  moreBannersContent: {
    minWidth: 45,
    backgroundColor: theme.colors.NEUTRAL_7,
    borderRadius: theme.radius.medium,
    padding: theme.spacing['2'],
    ...theme.elevations['4'],
  },
  moreBannersText: {
    textAlign: 'center',
  },
});

// ────────────────────────────────────────────────────────────────────────────────

type NoticeBannersStackProps = Readonly<{
  targetId?: string;
  bannerPadding?: ComponentProps<typeof NoticeBanner>['padding'];
  bannerVariation?: ComponentProps<typeof NoticeBanner>['variation'];
  shouldUseFullWindowOverlay?: boolean; // iOS-only
  style?: ComponentProps<typeof NoticeBannersStackWrapper>['style'];
}> &
  Omit<ViewProps, 'style'>;

type NoticeBannersStackActiveBannerProps = {
  banner: NoticeBannersStackItem;
  stack: NoticeBannersStackItems;
  bannerPadding: NonNullable<NoticeBannersStackProps['bannerPadding']>;
  bannerVariation: NoticeBannersStackProps['bannerVariation'];
  onRemove: (bannerId: string | undefined) => void;
};

type NoticeBannersStackItem = NoticeBannersStackItems[number];

type NoticeToastItem = NoticeBannersStackItem & { id: string };
